/* ***************************************************************** */
/*                                                                   */
/* Licensed Materials - Property of Merative                         */
/*                                                                   */
/* (C) Copyright Merative Corp. 2025                                 */
/*                                                                   */
/* ***************************************************************** */
import React, {Fragment, useCallback, useEffect, useRef} from 'react';
import {Tab, TabList, Tabs} from "@carbon/react";
import {useDispatch, useSelector} from "react-redux";
import {useHistory, useLocation} from "react-router-dom";
import {
    makeClearSelectedItemsAction,
} from "mdx-ivcomp-common-ui/dist/src/components/ivpicklist/iv-picklist-redux/iv-picklist-redux";
import {get} from "../../../../../utils"
import EnteralFormulaPicklist from "./components/EnteralFormulaPicklist";
import FormulaViewButtons from "./components/FormulaViewButtons";
import FormulaDisplayChoice from "./components/FormulaDisplayChoice";
import {makeSetCurrentPicklistIdAction, selectCurrentPicklistId, makeSetDisplayChoiceAction} from "./redux/formula_search_redux";
import {
    NO_NEOPEDS_SUBSCRIPTIONS_MESSAGE,
    RESULT_TYPE_NEOFAX,
    RESULT_TYPE_PEDIATRICS,
    FORMULA_SEARCH_PANEL_NAME,
    NEOFAX_FORMULA_PICKLIST_ID,
    PEDIATRICS_FORMULA_PICKLIST_ID,
    PER_100_CAL
} from "../../../common/constants";
import {updateTabParamInUrl} from "../../../utils";
import {computeInitialPicklistId} from "../../../utils";
import {TitleHeader} from "../../../common/components";

export const MORE_THAN_THREE_TITLE = "At most 3 Formulas!";
export const MORE_THAN_THREE_MESSAGE = "You can only select up to 3 formulas at a time. Please remove one of the selected formulas to add a new one.";


export const EnteralFormulasSearchPage = React.memo(({ shouldReset }) => {
    //  React utilities ...
    const dispatch = useDispatch();
    const history = useHistory();
    const login_page = useSelector((state) => state.login_page || {});
    const hasNeonatalSubscription = get(login_page, 'mdxAuthenticationJson.neonatalSubscription', 'no') === 'yes';
    const hasPediatricSubscription = get(login_page, 'mdxAuthenticationJson.pediatricsSubscription', 'no') === 'yes';

    const currentPicklistId = useSelector(selectCurrentPicklistId);

    const panelLocation = useLocation();
    const searchParams = new URLSearchParams(panelLocation.search);
    const tabFromUrl = searchParams.get("tab") || RESULT_TYPE_NEOFAX ; // neofax | pediatrics
    const wantedPicklistId = computeInitialPicklistId(tabFromUrl, hasNeonatalSubscription, hasPediatricSubscription);

    useEffect(() => {
        // update the state with the desired initial picklist id
        if (wantedPicklistId && currentPicklistId!==wantedPicklistId) {
            dispatch(makeSetCurrentPicklistIdAction(wantedPicklistId));
        }
    },
        [] // eslint-disable-line react-hooks/exhaustive-deps
    ); // run only once when the component mounts

    useEffect(() => {
        if (shouldReset) {
            dispatch(makeClearSelectedItemsAction(NEOFAX_FORMULA_PICKLIST_ID, FORMULA_SEARCH_PANEL_NAME));
            dispatch(makeClearSelectedItemsAction(PEDIATRICS_FORMULA_PICKLIST_ID, FORMULA_SEARCH_PANEL_NAME));
            dispatch(makeSetDisplayChoiceAction(PER_100_CAL));
        }
    }, [shouldReset, dispatch]);

    // DOM element refs ...
    const scrollingDivRef = useRef(null);

    const newTabIndexChangeHandler = useCallback( (click_event) => {
        const current_button = click_event.target.closest('button');
        if (!current_button) {
            return;
        }
        const button_label = current_button.getAttribute('label');
        if (!button_label) {
            return;
        }
        const tabLabel = button_label === 'neonatal-tab' ? RESULT_TYPE_NEOFAX : RESULT_TYPE_PEDIATRICS;
        updateTabParamInUrl(tabLabel, history);

        const newPicklistId = button_label === 'neonatal-tab' ? NEOFAX_FORMULA_PICKLIST_ID : PEDIATRICS_FORMULA_PICKLIST_ID;

        // clear the current search text
        dispatch(makeClearSelectedItemsAction(NEOFAX_FORMULA_PICKLIST_ID, FORMULA_SEARCH_PANEL_NAME));
        dispatch(makeClearSelectedItemsAction(PEDIATRICS_FORMULA_PICKLIST_ID, FORMULA_SEARCH_PANEL_NAME));

        dispatch(makeSetCurrentPicklistIdAction(newPicklistId))
        //eslint-disable-next-line react-hooks/exhaustive-deps
    },[dispatch]);

    const FormulaSearchTabs = () => {
        console.log('****************************************************** EnteralFormulasSearchPage rendered');
        const tabIndex = currentPicklistId === NEOFAX_FORMULA_PICKLIST_ID ? 0 : 1;
        const formulaTypeName = currentPicklistId === NEOFAX_FORMULA_PICKLIST_ID ? 'Neonatal' : 'Pediatric';
        const shouldFetchData = currentPicklistId === NEOFAX_FORMULA_PICKLIST_ID ? hasNeonatalSubscription : hasPediatricSubscription;
        return <>
            <TitleHeader
              id="formula-header"
              subTitle="NeoFax® / Pediatrics"
              mainTitle="Enteral Formula Comparison"
            />
            <Tabs
                defaultSelectedIndex={tabIndex}
                selectedIndex={tabIndex}
                tabContentClassName="tabs-inner-content"
            >
                <div className="border-line" id="formula-neo-peds-tabs">
                    <TabList contained={true}  aria-label="Formula Search Tabs">


                            <Tab id="tab-1-formula"
                                 label="neonatal-tab"
                                 disabled={!hasNeonatalSubscription}
                                 onClick={newTabIndexChangeHandler}
                                 style={hasNeonatalSubscription?{}:{display: 'none'}}
                            >
                                Neonatal
                            </Tab>
                            <Tab id="tab-2-formula"
                                 label="pediatric-tab"
                                 disabled={!hasPediatricSubscription}
                                 onClick={newTabIndexChangeHandler}
                                 style={hasPediatricSubscription?{}:{display: 'none'}}
                            >
                                Pediatric
                            </Tab>
                    </TabList>
                </div>

                {
                    !(hasNeonatalSubscription || hasPediatricSubscription) &&
                    <div className="formula-search-no-subscription">
                        {NO_NEOPEDS_SUBSCRIPTIONS_MESSAGE}
                    </div>
                }
                {
                    (hasNeonatalSubscription || hasPediatricSubscription) &&
                    <>
                        <FormulaDisplayChoice/>
                        <div className="formula-search-panel">
                            <EnteralFormulaPicklist
                                formulaTypeName={formulaTypeName}
                                picklistId={currentPicklistId}
                                shouldFetchData={shouldFetchData}
                            />
                        </div>
                        <FormulaViewButtons/>
                    </>
                }
            </Tabs>
        </>
    }

    return (<div ref={scrollingDivRef} className="formula-search-container">
            <div
                className="mdx-scroll-area mdx-scroll-area-ie neofax-pediatrics-formula-page-container"
                data-testid="neofax-pediatrics-formula-page-container"
            >
                <FormulaSearchTabs/>
            </div>
        </div>
    );
});
